<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const { $bus, $constants, $stores, $attrs } = proxy;
const currentPage = ref(0);
const pageSize = computed(() => transactionsState.value?.limit || 10);

const transactionsState = computed(() => $stores.transactions?.state?.list_all);

const totalPages = computed(() => {
  const total = transactionsState.value?.total || 0;
  return Math.ceil(total / pageSize.value);
});

function changePage(page) {
  if (page < 0) return;
  if (page >= totalPages.value) return;

  currentPage.value = page;

  $bus.emit($constants.EVENTS.TRANSACTIONS.LIST_ALL.REFRESH, {
    page,
    userId: $attrs.userId
  });
}

/**
 * Optionnel :
 * reset la page quand on relance un refresh global
 */
watch(
  () => transactionsState.value?.resetPagination,
  (val) => {
    if (val) {
      currentPage.value = 0;
    }
  }
);
</script>

<template>
  <VBusHelper
    :emitOnMounted="$constants.EVENTS.TRANSACTIONS.LIST_ALL.REFRESH"
    :dataOnMounted="{ userId: $attrs.userId }"
  />
  <TransactionsLoadingError
    @refresh="() => $bus.emit($constants.EVENTS.TRANSACTIONS.LIST_ALL.REFRESH)"
    v-if="$stores.transactions?.state?.list_all?.error"
  >
    <template #errorDescription>
      {{ $stores.transactions?.state?.list_all?.error }}
    </template>
  </TransactionsLoadingError>
  <div
    v-else-if="$stores.transactions?.state?.list_all?.list.length"
    class="space-y-4"
  >
    <TransactionItem
      @click="$bus.emit($constants.EVENTS.TRANSACTIONS.DETAIL.SHOW, trx)"
      v-for="trx in $stores.transactions?.state?.list_all?.list"
      :item="trx"
    />

    <div
      v-if="totalPages > 1"
      class="px-6 py-4 bg-white/50 backdrop-blur-sm border-t border-gray-100 flex justify-between items-center rounded-b-xl"
    >
      <span class="text-sm font-medium text-gray-500">
        Page <span class="text-purple-600">{{ currentPage + 1 }}</span> sur
        <span class="text-gray-900">{{ totalPages }}</span>
      </span>
      <div class="flex items-center space-x-3">
        <button
          :disabled="currentPage === 0"
          @click="changePage(currentPage - 1)"
          class="px-4 py-2 rounded-lg transition-all duration-200 flex items-center justify-center"
          :class="
            currentPage === 0
              ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
              : 'bg-purple-50 text-purple-600 hover:bg-purple-100 active:scale-95'
          "
        >
          <span class="mr-2 text-lg leading-none">&larr;</span>
          <span class="text-sm font-semibold">Précédent</span>
        </button>
        <button
          :disabled="currentPage >= totalPages - 1"
          @click="changePage(currentPage + 1)"
          class="px-4 py-2 rounded-lg transition-all duration-200 flex items-center justify-center"
          :class="
            currentPage >= totalPages - 1
              ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
              : 'bg-purple-600 text-white hover:bg-purple-700 shadow-sm shadow-purple-200 active:scale-95'
          "
        >
          <span class="mr-2 text-sm font-semibold">Suivant</span>
          <span class="text-lg leading-none">&rarr;</span>
        </button>
      </div>
    </div>
  </div>
  <div
    v-else-if="$stores.transactions?.state?.list_all?.loading"
    class="space-y-4 shimmerContainer"
  >
    <TransactionItem v-for="trx in 9" :item="trx" />
  </div>
  <EmptyTransactions
    @refresh="
      () =>
        $bus.emit($constants.EVENTS.TRANSACTIONS.LIST_ALL.REFRESH, {
          page: currentPage,
          userId: $attrs.userId
        })
    "
    v-else
  />
</template>
